import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import dayjs from "dayjs";
import { SalikSection } from "./fines/SalikSection";
import { FinesSection } from "./fines/FinesSection";
import { DisputeDialog } from "./fines/DisputeDialog";

export const FinesPanel = ({ bookings }) => {
  const started = (bookings || []).filter((b) => b.status !== "cancelled" && dayjs(b.pickup_datetime).isBefore(dayjs()));
  const [sel, setSel] = useState(started[0]?.booking_ref || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disputeFine, setDisputeFine] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback((ref) => {
    if (!ref) return;
    setLoading(true);
    api.get(`/bookings/${ref}/charges`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(sel); }, [sel, load]);

  const submitDispute = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/fines/disputes", { booking_ref: sel, fine_id: disputeFine.id, reason });
      toast.success(`Dispute ${d.dispute_id} submitted`);
      setDisputeFine(null);
      setReason("");
      load(sel);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (started.length === 0)
    return (
      <div className="bg-white border border-[#e2ebe4] rounded-lg p-10 text-center text-sm text-[#57685e]" data-testid="fines-empty">
        Toll and fine charges appear once a rental has started. Authority charges may also arrive after your rental ends.
      </div>
    );

  return (
    <div data-testid="fines-panel">
      <div className="max-w-md mb-5">
        <label className="field-label">Select booking</label>
        <select className="field-input" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="fines-booking-select">
          {started.map((b) => (
            <option key={b.booking_ref} value={b.booking_ref}>
              {b.booking_ref} · {b.vehicle.name} · {dayjs(b.pickup_datetime).format("DD MMM")}–{dayjs(b.return_datetime).format("DD MMM")}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>}
      {!loading && data && (
        <div className="grid lg:grid-cols-2 gap-6">
          <SalikSection data={data} />
          <FinesSection data={data} onDispute={(f) => { setDisputeFine(f); setReason(""); }} />
        </div>
      )}

      <div className="mt-6 bg-[#f4f7f4] border border-[#e2ebe4] rounded-lg p-4 text-xs text-[#144733]" data-testid="fines-policy-box">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#009900]" />
          <div className="space-y-1">
            <p><strong>Policies:</strong> You are liable for all tolls and fines during your rental. UAE toll gates — Salik in Dubai and Darb in Abu Dhabi — cost from AED 4 per crossing (Salik is capped at AED 24 a day). A 25% admin fee applies to tolls; a 10% + VAT admin fee applies to fines. Please don't pay an authority directly once Europcar has settled a fine — check with us first.</p>
            <p><strong>Dispute deadlines:</strong> Dubai 30 days · Abu Dhabi 60 days · Sharjah / Ajman / RAK / Fujairah / UAQ 15 days from notification. Successful disputes are refunded within 15–45 days.</p>
          </div>
        </div>
      </div>

      <DisputeDialog fine={disputeFine} reason={reason} setReason={setReason} busy={busy}
        onSubmit={submitDispute} onClose={() => setDisputeFine(null)} />
    </div>
  );
};
